import { motion, useInView, type Variants } from "framer-motion";
import { useRef } from "react";
import {
    Users,
    MessageSquare,
    Calendar,
    Heart,
    Award,
    BookOpen,
    Briefcase,
    Globe,
    ArrowUpRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const features = [
    {
        icon: Users,
        title: "Member Directory",
        description: "Connect with colleagues, mentors, and civic leaders across your city.",
        href: "/directory",
        gradient: "from-accent/20 to-accent/5",
        iconBg: "bg-accent/15",
        iconColor: "text-accent",
    },
    {
        icon: MessageSquare,
        title: "Channels & Messaging",
        description: "Engage in topic-based discussions and collaborative workspaces.",
        href: "/channels",
        gradient: "from-primary/20 to-primary/5",
        iconBg: "bg-primary/15",
        iconColor: "text-primary",
    },
    {
        icon: Calendar,
        title: "Events & Gatherings",
        description: "Discover networking events, workshops, and community gatherings.",
        href: "/events",
        gradient: "from-accent/20 to-accent/5",
        iconBg: "bg-accent/15",
        iconColor: "text-accent",
    },
    {
        icon: Heart,
        title: "Volunteering",
        description: "Find meaningful volunteer opportunities and contribute your skills.",
        href: "/volunteering",
        gradient: "from-primary/20 to-primary/5",
        iconBg: "bg-primary/15",
        iconColor: "text-primary",
    },
    {
        icon: Award,
        title: "Recognition & Awards",
        description: "Celebrate achievements and recognize outstanding contributions.",
        href: "/dashboard",
        gradient: "from-accent/20 to-accent/5",
        iconBg: "bg-accent/15",
        iconColor: "text-accent",
    },
    {
        icon: BookOpen,
        title: "SOP Library",
        description: "Access standard operating procedures shared by professionals.",
        href: "/sop-library",
        gradient: "from-primary/20 to-primary/5",
        iconBg: "bg-primary/15",
        iconColor: "text-primary",
    },
    {
        icon: Briefcase,
        title: "Sponsorship Hub",
        description: "Connect organizations with community initiatives seamlessly.",
        href: "/sponsorship",
        gradient: "from-accent/20 to-accent/5",
        iconBg: "bg-accent/15",
        iconColor: "text-accent",
    },
    {
        icon: Globe,
        title: "Cross-City Network",
        description: "Collaborate with civic professionals from other cities.",
        href: "/organizations",
        gradient: "from-primary/20 to-primary/5",
        iconBg: "bg-primary/15",
        iconColor: "text-primary",
    },
];

const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.2,
        },
    },
};

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
            ease: [0.4, 0, 0.2, 1] as const,
        },
    },
};

export const FeaturesSection = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });
    const navigate = useNavigate();

    return (
        <section id="features" className="section-padding relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-muted/30 via-muted/20 to-transparent" />
            <div className="absolute inset-0 dot-pattern opacity-30" />

            <div className="container mx-auto relative z-10" ref={ref}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center max-w-3xl mx-auto mb-10 md:mb-14"
                >
                    <motion.span
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="inline-block text-accent font-semibold text-xs uppercase tracking-widest mb-3 px-3 py-1 rounded-full bg-accent/10 border border-accent/20"
                    >
                        Features
                    </motion.span>
                    <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                        Everything You Need to{" "}
                        <span className="gradient-text">Build Community</span>
                    </h2>
                    <p className="text-muted-foreground text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
                        Powerful tools designed to foster connection, collaboration, and civic engagement at every level.
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
                >
                    {features.map((feature) => (
                        <motion.div
                            key={feature.title}
                            variants={itemVariants}
                            whileHover={{ y: -8, transition: { duration: 0.3 } }}
                            className="group"
                        >
                            <div className="relative glass-card rounded-xl md:rounded-2xl p-4 md:p-5 h-full overflow-hidden transition-all duration-500 hover:shadow-xl hover:border-accent/30">
                                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                                <div className="relative z-10">
                                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl ${feature.iconBg} flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                        <feature.icon className={`h-5 w-5 md:h-6 md:w-6 ${feature.iconColor}`} />
                                    </div>
                                    <div className="flex items-start justify-between mb-2">
                                        <h3 className="font-display font-semibold text-sm md:text-base text-foreground group-hover:text-primary transition-colors leading-tight">
                                            {feature.title}
                                        </h3>
                                    </div>
                                    <p className="text-muted-foreground text-xs md:text-sm leading-relaxed line-clamp-2">
                                        {feature.description}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};
